import React from 'react'
import { Table, Button, Tag, Space, Modal, message, Image, Card, Row, Col, Form, DatePicker, Input, Upload, Radio } from 'antd'
import { CheckCircleOutlined, ClockCircleOutlined, EyeOutlined, DeleteOutlined, PhoneOutlined, CalendarOutlined, TagOutlined, PlusOutlined } from '@ant-design/icons'
import axios from 'axios'

const { TextArea } = Input

function CustomerList({ customers, loading, onComplete, onRefresh, dateFilter, onDateFilterChange }) {
  const [selectedCustomer, setSelectedCustomer] = React.useState(null)
  const [detailModalVisible, setDetailModalVisible] = React.useState(false)
  const [completeModalVisible, setCompleteModalVisible] = React.useState(false)
  const [completingCustomer, setCompletingCustomer] = React.useState(null)
  const [completeForm] = Form.useForm()
  const [isMobile, setIsMobile] = React.useState(false)

  // 检测是否为移动端
  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // 日期格式化函数
  const formatDateToYYYYMMDD = (dateValue) => {
    if (!dateValue) return ''
    
    try {
      // 如果是ISO格式的日期字符串
      if (typeof dateValue === 'string' && dateValue.includes('T')) {
        return dateValue.split('T')[0]
      }
      
      // 如果是Date对象
      if (dateValue instanceof Date) {
        return dateValue.toISOString().split('T')[0]
      }
      
      // 如果是YYYY-MM-DD HH:MM格式
      if (typeof dateValue === 'string' && dateValue.includes(' ')) {
        return dateValue.split(' ')[0]
      }
      
      // 直接返回
      return dateValue
    } catch (e) {
      return dateValue
    }
  }

  const handleViewDetail = (customer) => {
    setSelectedCustomer(customer)
    setDetailModalVisible(true)
  }

  const handleDelete = async (customerId) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这个客户吗？此操作不可恢复。',
      onOk: async () => {
        try {
          await axios.delete(`/api/customers/${customerId}`)
          message.success('客户删除成功')
          onRefresh && onRefresh()
        } catch (error) {
          message.error('删除失败')
        }
      }
    })
  }

  const handleComplete = (customer) => {
    setCompletingCustomer(customer)
    setCompleteModalVisible(true)
    completeForm.setFieldsValue({
      completed_time: null,
      remark: '',
      photos: []
    })
  }

  const handleCompleteSubmit = async (values) => {
    try {
      // 处理照片上传
      const photoPaths = []
      if (values.photoFiles && values.photoFiles.fileList) {
        for (const file of values.photoFiles.fileList) {
          if (file.originFileObj) {
            const formData = new FormData()
            formData.append('photo', file.originFileObj)
            formData.append('customer_id', completingCustomer._id)
            
            const uploadResponse = await axios.post('/api/upload/photo', formData, {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            })
            
            if (uploadResponse.data.relative_path) {
              photoPaths.push(uploadResponse.data.relative_path)
            }
          }
        }
      }
      
      // 调用完成接口
      await onComplete(completingCustomer._id, {
        ...values,
        photoPaths: photoPaths
      })
      
      setCompleteModalVisible(false)
      setCompletingCustomer(null)
      completeForm.resetFields()
      message.success('客户标记为已完成')
    } catch (error) {
      message.error('操作失败')
    }
  }

  // PC端表格列配置
  const columns = [
    {
      title: '客户类型',
      dataIndex: 'customer_type',
      key: 'customer_type',
      width: 100,
      render: (type) => (
        <Tag color={type === 'online' ? 'blue' : 'green'}>
          {type === 'online' ? '线上预约' : '当面推销'}
        </Tag>
      )
    },
    {
      title: '客户姓名',
      dataIndex: 'name',
      key: 'name',
      width: 120
    },
    {
      title: '联系电话',
      dataIndex: 'phone',
      key: 'phone',
      width: 130
    },
    {
      title: '预约时间',
      dataIndex: 'appointment_time',
      key: 'appointment_time',
      width: 150
    },
    {
      title: '套餐类型',
      dataIndex: 'package_type',
      key: 'package_type',
      width: 120
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag 
          color={status === 'completed' ? 'green' : 'orange'}
          icon={status === 'completed' ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
        >
          {status === 'completed' ? '已完成' : '待处理'}
        </Tag>
      ),
      width: 100
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button 
            type="link" 
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
            size="small"
          >
            详情
          </Button>
          {record.status === 'pending' && (
            <Button 
              type="link" 
              onClick={() => handleComplete(record)}
              size="small"
            >
              完成
            </Button>
          )}
          <Button 
            type="link" 
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record._id)}
            size="small"
          >
            删除
          </Button>
        </Space>
      ),
      width: 180
    },
  ]

  // 移动端卡片布局
  const renderMobileList = () => (
    <div className="mobile-customer-list">
      {customers.map((customer) => (
        <Card 
          key={customer._id} 
          className="customer-card"
          style={{ marginBottom: 16 }}
        >
          <div className="customer-header">
            <div className="customer-name">{customer.name}</div>
            <div>
              <Tag color={customer.customer_type === 'online' ? 'blue' : 'green'} style={{ marginRight: 8 }}>
                {customer.customer_type === 'online' ? '线上预约' : '当面推销'}
              </Tag>
              <Tag 
                color={customer.status === 'completed' ? 'green' : 'orange'}
                icon={customer.status === 'completed' ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
              >
                {customer.status === 'completed' ? '已完成' : '待处理'}
              </Tag>
            </div>
          </div>
          
          <div className="customer-info">
            <div className="info-item">
              <PhoneOutlined />
              <span>{customer.phone}</span>
            </div>
            {customer.appointment_time && (
              <div className="info-item">
                <CalendarOutlined />
                <span>{customer.appointment_time}</span>
              </div>
            )}
            <div className="info-item">
              <TagOutlined />
              <span>{customer.package_type}</span>
            </div>
          </div>
          
          <div className="customer-actions">
            <Button 
              type="link" 
              icon={<EyeOutlined />}
              onClick={() => handleViewDetail(customer)}
              size="small"
            >
              详情
            </Button>
            {customer.status === 'pending' && (
              <Button 
                type="link" 
                onClick={() => handleComplete(customer)}
                size="small"
              >
                完成
              </Button>
            )}
            <Button 
              type="link" 
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(customer._id)}
              size="small"
            >
              删除
            </Button>
          </div>
        </Card>
      ))}
    </div>
  )

  // PC端表格布局
  const renderPcTable = () => (
    <Table
      columns={columns}
      dataSource={customers}
      loading={loading}
      rowKey="_id"
      pagination={{
        pageSize: 10,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total) => `共 ${total} 条记录`
      }}
      scroll={{ x: 800 }}
      size="middle"
    />
  )

  // 日期筛选器
  const dateFilterOptions = [
    { label: '全部客户', value: 'all' },
    { label: '今日完成', value: 'today' },
    { label: '昨日完成', value: 'yesterday' },
    { label: '近7天完成', value: 'last_7_days' }
  ]

  return (
    <>
      <div className="date-filter-section" style={{ marginBottom: 16 }}>
        <div className="filter-label">完成时间筛选：</div>
        <Radio.Group 
          value={dateFilter || 'all'} 
          onChange={(e) => onDateFilterChange && onDateFilterChange(e.target.value)}
          optionType="button"
          buttonStyle="solid"
        >
          {dateFilterOptions.map(option => (
            <Radio.Button key={option.value} value={option.value}>
              {option.label}
            </Radio.Button>
          ))}
        </Radio.Group>
      </div>

      {isMobile ? renderMobileList() : renderPcTable()}

      <Modal
        title="客户详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={600}
      >
        {selectedCustomer && (
          <div className="customer-detail">
            <div className="detail-section">
              <h4>基本信息</h4>
              <p><strong>客户类型：</strong>
                <Tag color={selectedCustomer.customer_type === 'online' ? 'blue' : 'green'}>
                  {selectedCustomer.customer_type === 'online' ? '线上预约' : '当面推销'}
                </Tag>
              </p>
              <p><strong>姓名：</strong>{selectedCustomer.name}</p>
              <p><strong>电话：</strong>{selectedCustomer.phone}</p>
              <p><strong>地址：</strong>{selectedCustomer.address}</p>
              <p><strong>套餐类型：</strong>{selectedCustomer.package_type}</p>
            </div>
            
            <div className="detail-section">
              <h4>预约信息</h4>
              <p><strong>预约时间：</strong>{formatDateToYYYYMMDD(selectedCustomer.appointment_time)}</p>
              <p><strong>状态：</strong>
                <Tag color={selectedCustomer.status === 'completed' ? 'green' : 'orange'}>
                  {selectedCustomer.status === 'completed' ? '已完成' : '待处理'}
                </Tag>
              </p>
              {selectedCustomer.completed_at && (
                <p><strong>完成时间：</strong>{formatDateToYYYYMMDD(selectedCustomer.completed_at)}</p>
              )}
              {selectedCustomer.completed_time && (
                <p><strong>办理时间：</strong>{formatDateToYYYYMMDD(selectedCustomer.completed_time)}</p>
              )}
            </div>
            
            {selectedCustomer.remark && (
              <div className="detail-section">
                <h4>初始备注</h4>
                <p>{selectedCustomer.remark}</p>
              </div>
            )}
            
            {selectedCustomer.completed_remark && (
              <div className="detail-section">
                <h4>办理备注</h4>
                <p>{selectedCustomer.completed_remark}</p>
              </div>
            )}
            
            {selectedCustomer.photo_paths && selectedCustomer.photo_paths.length > 0 && (
              <div className="detail-section">
                <h4>照片 ({selectedCustomer.photo_paths.length}张)</h4>
                <div className="photo-gallery">
                  {selectedCustomer.photo_paths.map((photoPath, index) => (
                    <Image
                      key={index}
                      width={100}
                      src={`http://localhost:5000${photoPath}`}
                      alt={`客户照片 ${index + 1}`}
                      placeholder={
                        <div style={{ width: 100, height: 100, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          加载中...
                        </div>
                      }
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* 完成客户模态框 */}
      <Modal
        title="完成客户办理信息"
        open={completeModalVisible}
        onCancel={() => {
          setCompleteModalVisible(false)
          setCompletingCustomer(null)
          completeForm.resetFields()
        }}
        footer={null}
        width={600}
      >
        {completingCustomer && (
          <Form
            form={completeForm}
            layout="vertical"
            onFinish={handleCompleteSubmit}
          >
            <div className="customer-info-summary" style={{ marginBottom: 16, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 6 }}>
              <h4>客户基本信息</h4>
              <Row gutter={16}>
                <Col span={12}>
                  <p><strong>姓名：</strong>{completingCustomer.name}</p>
                </Col>
                <Col span={12}>
                  <p><strong>电话：</strong>{completingCustomer.phone}</p>
                </Col>
                <Col span={24}>
                  <p><strong>地址：</strong>{completingCustomer.address}</p>
                </Col>
              </Row>
            </div>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="completed_name"
                  label="客户姓名（确认）"
                  rules={[{ required: true, message: '请确认客户姓名' }]}
                  initialValue={completingCustomer.name}
                >
                  <Input 
                    placeholder="请确认客户姓名"
                    size="large"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="completed_time"
                  label="办理完成时间"
                  rules={[{ required: true, message: '请选择办理完成时间' }]}
                >
                  <DatePicker 
                    placeholder="选择完成时间"
                    style={{ width: '100%' }}
                    size="large"
                    format="YYYY-MM-DD"
                    disabledDate={(current) => {
                      return current && current > Date.now()
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="new_phone_number"
              label="新开号码"
              rules={[
                { required: true, message: '请输入新开号码' },
                { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号码格式' }
              ]}
            >
              <Input 
                placeholder="请输入新开手机号码"
                size="large"
                maxLength={11}
              />
            </Form.Item>

            <Form.Item
              name="remark"
              label="办理备注"
            >
              <TextArea 
                placeholder="请输入办理备注信息（如套餐详情、办理过程等）"
                rows={3}
                size="large"
              />
            </Form.Item>

            <Form.Item
              name="photoFiles"
              label="上传照片"
              extra="请上传客户办理过程中的照片，最多可上传10张照片，每张不超过10MB"
            >
              <Upload
                listType="picture-card"
                beforeUpload={() => false}
                maxCount={10}
                multiple
                accept="image/*"
              >
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>上传照片</div>
                </div>
              </Upload>
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
              <Button onClick={() => {
                setCompleteModalVisible(false)
                setCompletingCustomer(null)
                completeForm.resetFields()
              }} style={{ marginRight: 8 }}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                确认完成办理
              </Button>
            </Form.Item>
          </Form>
        )}
      </Modal>
    </>
  )
}

export default CustomerList